import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { useState } from 'react';
import useGetCountries from '../../../hooks/useGetCountries';
import useGetTopDestination from '../../../hooks/useTopDestination';
import { Link } from 'react-router-dom';

const toRouteSlug = (routeName: string) =>
  routeName.toLowerCase().replace(/\s+/g, '-');

const DestinationCard = ({
  routeName,
  countryName,
  routeImageUrl,
  className = '',
}: {
  routeName: string;
  countryName: string;
  routeImageUrl: string;
  className?: string;
}) => (
  <Link
    to={`/routes/${toRouteSlug(routeName)}`}
    className={`relative block overflow-hidden rounded-2xl no-underline ${className}`}
  >
    <img
      className="h-full w-full object-cover"
      src={routeImageUrl}
      alt={`${routeName}, ${countryName}`}
    />
    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent px-4 pb-6 pt-16 text-center text-neutral-00">
      <span className="text-sm">{countryName}</span>
      <span className="mt-1 block text-2xl font-bold">{routeName}</span>
    </div>
  </Link>
);

const TopDiveDestinations = () => {
  const [currentPage, setCurrentPage] = useState(0);

  const { topDestination, topDestinationLoading } = useGetTopDestination();
  const { countries, countryLoading } = useGetCountries();

  if (topDestinationLoading || countryLoading) return null;

  const data = topDestination?.topDestination || [];
  if (data.length === 0) return null;

  const tripsPerPage = 4;
  const totalPages = Math.ceil(data.length / tripsPerPage);

  const handlePreviousPage = () => {
    setCurrentPage(page => (page - 1 + totalPages) % totalPages);
  };

  const handleNextPage = () => {
    setCurrentPage(page => (page + 1) % totalPages);
  };

  const findCountryNameById = (countryId: number) => {
    const country = countries?.countries.find(
      country => country.countryId === countryId,
    );
    return country ? country.countryName : 'Country not found';
  };

  const pageIndex = Math.min(currentPage, totalPages - 1);
  const startIndex = pageIndex * tripsPerPage;
  const routeToDisplay = data.slice(startIndex, startIndex + tripsPerPage);

  return (
    <section id="destinations" className="mt-10 px-4 sm:px-6 lg:mt-12 lg:px-12">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 flex items-start justify-between gap-4 lg:mb-8">
          <div>
            <h2 className="text-xl font-bold lg:text-2xl">
              Top dive destinations
            </h2>
            <p className="mt-1 text-sm text-neutral-300">
              Explore routes divers book most often.
            </p>
          </div>
          {totalPages > 1 && (
            <div className="hidden shrink-0 lg:flex">
              <button
                type="button"
                className="mx-1 flex min-h-11 min-w-11 items-center justify-center rounded-full border"
                onClick={handlePreviousPage}
                aria-label="Previous destinations"
              >
                <IconChevronLeft />
              </button>
              <button
                type="button"
                className="mx-1 flex min-h-11 min-w-11 items-center justify-center rounded-full border"
                onClick={handleNextPage}
                aria-label="Next destinations"
              >
                <IconChevronRight />
              </button>
            </div>
          )}
        </div>

        {/* Desktop: equal-width grid that fills the content container */}
        <div className="hidden gap-4 pb-2 lg:grid lg:grid-cols-4">
          {routeToDisplay.map(route => (
            <DestinationCard
              key={route.routeId}
              routeName={route.routeName}
              countryName={findCountryNameById(route.countryId)}
              routeImageUrl={route.routeImageUrl || ''}
              className="h-[25rem] w-full"
            />
          ))}
        </div>

        {/* Mobile: horizontal scroll-snap — one card in focus, no peeking overlap */}
        <div className="-mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-2 scrollbar-hide lg:hidden">
          {data.map(route => (
            <DestinationCard
              key={route.routeId}
              routeName={route.routeName}
              countryName={findCountryNameById(route.countryId)}
              routeImageUrl={route.routeImageUrl || ''}
              className="h-[22rem] w-[85vw] max-w-sm shrink-0 snap-center"
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TopDiveDestinations;
